import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import { cx, styled } from "simplydo/react/styled";
import { CardContent, CardHeader } from "simplydo/react/ui/card";
import { H3 } from "simplydo/react/ui/header";
import { Label } from "simplydo/react/ui/label";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

export const StyledCardContent = styled(CardHeader)`
  &.is-assessment {
    border: none !important;
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.violet.solid} !important;
  }
`;

type SectionHeaderProps = {
  section: NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number];
  assessment?: NonNullable<OpenAPI.Schemas["Idea"]["assessments"]>[number];
  canViewEdi: boolean;
  canEdit: boolean;
  isAuthor: boolean;
  isAssessor?: boolean;
};

const SectionHeader = ({
  section,
  assessment,
  canViewEdi,
  canEdit,
  isAuthor,
  isAssessor,
}: SectionHeaderProps) => {
  const { t } = useTranslation();

  const hasAssessmentScore = (fieldId: string) => {
    const score = assessment?.assessment?.[fieldId]?.score;
    if (score === undefined || score === null) {
      return false;
    }
    if (typeof score === "string") {
      return score !== "";
    }
    return true;
  };

  return (
    <StyledCardContent
      className={cx(section.type === "assessment" && "is-assessment")}
    >
      <H3>{section.name}</H3>
      {isAssessor && section.type === "assessment" ? (
        <Label
          color={
            section.fields.filter((field) => hasAssessmentScore(field.id))
              .length ===
            section.fields.filter((field) => field.type === "assessment").length
              ? "green"
              : undefined
          }
        >
          Assessed{" "}
          {
            section.fields.filter((field) => hasAssessmentScore(field.id))
              .length
          }{" "}
          /{" "}
          {section.fields.filter((field) => field.type === "assessment").length}
        </Label>
      ) : null}

      {section.type === "edi" ? (
        !isAuthor && !canViewEdi && canEdit ? (
          <Message color="orange">
            <MessageHeader>EDI privacy notice</MessageHeader>
            <MessageContent>
              {t("ideas.edi.header")}. Its content is hidden because you do not
              have permissions to view or edit the information in this section.
              For privacy reasons, only {t("generic.idea")} authors and
              administrators can see this data. If you believe you should have
              access, please contact your organisation&apos;s administrator.
            </MessageContent>
          </Message>
        ) : (
          <Message color="green">
            <MessageHeader>{t("ideas.edi.header")}</MessageHeader>
            <MessageContent>{t("ideas.edi.info")}</MessageContent>
          </Message>
        )
      ) : null}
    </StyledCardContent>
  );
};

export default SectionHeader;
